import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Select } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { formatName } from '@/utils/nameFormatter';
import { selectCurrentAdmin } from '@/redux/auth/selectors';

/**
 * DashboardFilters
 * Top filter bar — Date Range, Project Owner, Project, Assignee selectors.
 * Uses Ant Design icons only — no Material Symbols dependency.
 */
export default function DashboardFilters({
    filters,
    onFilterChange,
    projects = [],
    employees = [],
    canViewAllTasks = true,
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const currentAdminId = currentAdmin?._id || currentAdmin?.id;
    const handleChange = (key, value) => onFilterChange({ ...filters, [key]: value });

    // Filter employees for the Assignee dropdown:
    // If not elevated / canViewAllTasks is false, scope to employees belonging to accessible projects + currentAdmin
    const relevantEmployees = useMemo(() => {
        if (canViewAllTasks || !projects.length) return employees;
        const memberIdSet = new Set();
        if (currentAdminId) memberIdSet.add(String(currentAdminId));
        projects.forEach((p) => {
            if (p.projectManager) memberIdSet.add(String(p.projectManager._id || p.projectManager));
            if (p.createdBy) memberIdSet.add(String(p.createdBy._id || p.createdBy));
            (p.members || []).forEach((m) => memberIdSet.add(String(m._id || m)));
            (p.collaborators || []).forEach((c) => memberIdSet.add(String(c._id || c)));
        });
        return employees.filter((emp) => memberIdSet.has(String(emp._id || emp.id)));
    }, [employees, projects, canViewAllTasks, currentAdminId]);

    // Relevant project owners
    const relevantOwners = useMemo(() => {
        if (canViewAllTasks || !projects.length) return employees;
        const ownerIdSet = new Set();
        projects.forEach((p) => {
            if (p.projectManager) ownerIdSet.add(String(p.projectManager._id || p.projectManager));
            if (p.createdBy) ownerIdSet.add(String(p.createdBy._id || p.createdBy));
        });
        return employees.filter((emp) => ownerIdSet.has(String(emp._id || emp.id)));
    }, [employees, projects, canViewAllTasks]);

    const vanillaSelectClass = 'bg-white dark:bg-[#1e293b] border-none text-xs sm:text-sm font-semibold text-[#191c1d] dark:text-white rounded-xl focus:ring-2 focus:ring-[#28b5fd] py-2 sm:py-2.5 shadow-sm cursor-pointer outline-none';

    return (
        <div className="flex flex-nowrap items-center gap-2 sm:gap-2.5 bg-[#f3f4f5] dark:bg-[#1e293b] p-1.5 sm:p-2 rounded-2xl max-w-full overflow-x-auto">

            {/* Date Range */}
            <div className="relative shrink-0">
                <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-[#0f172a] text-xs font-bold text-[#424750] dark:text-slate-400 px-1 rounded z-10 pointer-events-none">
                    Date Range
                </label>
                <select
                    value={filters.dateRange || 'All Time'}
                    onChange={(e) => handleChange('dateRange', e.target.value)}
                    className={`${vanillaSelectClass} min-w-[120px] sm:min-w-[130px] pl-2.5 pr-7`}
                >
                    <option>All Time</option>
                    <option>This Week</option>
                    <option>Today</option>
                    <option>Last 30 Days</option>
                </select>
            </div>

            {/* Project Owner */}
            <div className="relative shrink-0">
                <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-[#0f172a] text-xs font-bold text-[#424750] dark:text-slate-400 px-1 rounded z-10 pointer-events-none">
                    Project Owner
                </label>
                <Select
                    placeholder="All Owners"
                    allowClear
                    showSearch
                    value={filters.owner || undefined}
                    onChange={(value) => handleChange('owner', value || '')}
                    className="w-[125px] sm:w-[135px]"
                    style={{
                        height: 40,
                    }}
                    options={[
                        {
                            label: 'All Owners',
                            value: '',
                        },
                        ...relevantOwners.map((emp) => ({
                            label: emp.fullName || formatName(`${emp.name} ${emp.surname || ''}`.trim()),
                            value: emp._id || emp.id,
                        })),
                    ]}
                    optionLabelProp="label"
                />
            </div>

            {/* Project */}
            <div className="relative shrink-0">
                <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-[#0f172a] text-xs font-bold text-[#424750] dark:text-slate-400 px-1 rounded z-10 pointer-events-none">
                    Project
                </label>
                <Select
                    placeholder="All Projects"
                    allowClear
                    showSearch
                    value={filters.projectId || undefined}
                    onChange={(value) => handleChange('projectId', value || '')}
                    className="w-[135px] sm:w-[145px]"
                    style={{
                        height: 40,
                    }}
                    options={[
                        {
                            label: 'All Projects',
                            value: '',
                        },
                        ...projects.map((p) => ({
                            label: p.name,
                            value: p._id,
                        })),
                    ]}
                    optionLabelProp="label"
                />
            </div>

            {/* Assignee */}
            <div className="relative shrink-0">
                <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-[#0f172a] text-xs font-bold text-[#424750] dark:text-slate-400 px-1 rounded z-10 pointer-events-none">
                    Assignee
                </label>
                <Select
                    placeholder={canViewAllTasks ? 'All Assignees' : 'All Team Tasks'}
                    allowClear
                    showSearch
                    value={filters.assignee || undefined}
                    onChange={(value) => handleChange('assignee', value || '')}
                    className="w-[125px] sm:w-[135px]"
                    style={{
                        height: 40,
                    }}
                    options={[
                        {
                            label: canViewAllTasks ? 'All Assignees' : 'All Team Tasks',
                            value: '',
                        },
                        ...(currentAdminId
                            ? [
                                {
                                    label: '★ Assigned to Me',
                                    value: currentAdminId,
                                },
                            ]
                            : []),
                        ...relevantEmployees
                            .filter((emp) => String(emp._id || emp.id) !== String(currentAdminId))
                            .map((emp) => ({
                                label: emp.fullName || formatName(`${emp.name} ${emp.surname || ''}`.trim()),
                                value: emp._id || emp.id,
                            })),
                    ]}
                    optionLabelProp="label"
                />
            </div>




            {/* Work Item Type (Section 25) */}
            <div className="relative shrink-0">
                <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-[#0f172a] text-xs font-bold text-[#424750] dark:text-slate-400 px-1 rounded z-10 pointer-events-none">
                    Work Item Type
                </label>
                <select
                    value={filters.itemType || ''}
                    onChange={(e) => handleChange('itemType', e.target.value)}
                    className={`${vanillaSelectClass} min-w-[120px] sm:min-w-[130px] pl-2.5 pr-7`}
                >
                    <option value="">All Types</option>
                    <option value="task">Tasks</option>
                    <option value="milestone">Milestones</option>
                </select>
            </div>

            {/* Filter button */}
            <button className="bg-[#e7e8e9] dark:bg-[#334155] hover:bg-[#e1e3e4] dark:hover:bg-[#475569] p-2 sm:p-2.5 rounded-xl transition-all shrink-0 flex items-center justify-center">
                <FilterOutlined className="text-[#191c1d] dark:text-white" style={{ fontSize: 16 }} />
            </button>
        </div>
    );
}
